import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Input from '@mui/material/Input';
import { Pagination, Select, MenuItem } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import usePageNavigation from '../../hooks/usePageNavigation';

import { getUsersList } from '../../services/requests/GraphRequests';

import { queryLimit } from '../../constants';
import { ROLE } from '../../constants';

export default function UserList({ role }) {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const { pageNumber = '1' } = useParams();
    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(null);
    const [usersStart, setUsersStart] = useState(0);
    const redirectLink = 'client/users';

    const filtersDefaultVal = {
        id: undefined,
        simpalsLogin: '',
        name: '',
        role: '',
        start: usersStart,
        limit: queryLimit,
    };

    const [filters, setFilters] = useState(filtersDefaultVal);

    usePageNavigation(
        setCurrentPage,
        setUsersStart,
        pageNumber,
        queryLimit,
        pageCount,
        navigate,
        redirectLink,
    );

    const handlePagination = (event, page) => {
        navigate(`/${redirectLink}/${page}`);
    };

    const getUsers = () => {
        getUsersList(filters)
            .then(data => {
                const { data: queryData, count: pageCount } =
                    data.data.getUsers;
                setPageCount(Math.ceil(pageCount / queryLimit));
                setUserList(queryData);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                // eslint-disable-next-line no-console
                console.log(error.message ? error.message : error);
            });
    };

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: value,
        }));
    };

    const handleClearValues = () => {
        setFilters(filtersDefaultVal);
    };

    useEffect(() => {
        getUsers();
    }, [currentPage, filters]);

    if (loading) {
        return (
            <Container align='center' sx={{ mt: 10 }}>
                <CircularProgress />
            </Container>
        );
    }
    if (!userList || role !== ROLE.director) {
        return false;
    }

    return (
        <Box
            sx={{ flexGrow: 1, marginTop: 3, marginLeft: 10, marginBottom: 10 }}
        >
            <Grid container sx={{ marginTop: 2 }} spacing={4}>
                <Grid item xs={6}>
                    <Typography
                        sx={{ fontSize: 24 }}
                        color='pimary'
                        gutterBottom
                    >
                        {t('list_users')}
                    </Typography>
                </Grid>
                <Grid item xs={5} align='right'>
                    <Button
                        variant='outlined'
                        onClick={() => {
                            navigate('/client/new-user');
                        }}
                    >
                        {t('add_user')}
                    </Button>
                </Grid>
            </Grid>
            <Grid
                container
                columnSpacing={5}
                sx={{ marginBottom: 2, alignItems: 'center', width: '100%' }}
            >
                <Grid item xs={2}>
                    <Input
                        name='id'
                        placeholder={t('id')}
                        type='number'
                        fullWidth
                        value={filters.id}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Input
                        name='simpalsLogin'
                        placeholder={t('simpals_login')}
                        type='text'
                        fullWidth
                        value={filters.simpalsLogin}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Input
                        name='name'
                        placeholder={t('name')}
                        type='text'
                        fullWidth
                        value={filters.name}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Select
                        name='role'
                        value={filters.role}
                        displayEmpty
                        fullWidth
                        onChange={handleInputChange}
                    >
                        <MenuItem value=''>{t('select_role')}</MenuItem>
                        {Object.entries(ROLE).map(([key, value]) => (
                            <MenuItem key={key} value={value}>
                                {t(value.toLowerCase())}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <div style={{ cursor: 'pointer', margin: '0 20px' }}>
                    <ClearIcon
                        fontSize='medium'
                        onClick={() => handleClearValues()}
                    />
                </div>
            </Grid>
            <Grid container spacing={2} sx={{ marginTop: 3 }} align='left'>
                <Grid item xs={11}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('id')}</TableCell>
                                    <TableCell>{t('simpals_login')}</TableCell>
                                    <TableCell>{t('roles')}</TableCell>
                                    <TableCell>{t('name')}</TableCell>
                                    <TableCell>{t('organization')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userList.map(user => {
                                    return (
                                        <TableRow
                                            hover
                                            key={user.id}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td, &:last-child th':
                                                    { border: 0 },
                                            }}
                                            onClick={() =>
                                                navigate(
                                                    `/client/edit-user/${user.id}`,
                                                    {
                                                        state: {
                                                            id: user.id,
                                                        },
                                                    },
                                                )
                                            }
                                        >
                                            <TableCell>{user.id}</TableCell>
                                            <TableCell>
                                                {user.simpalsLogin}
                                            </TableCell>
                                            <TableCell>
                                                {user.accesses?.map(access => {
                                                    return (
                                                        <div key={access?.id}>
                                                            {t(
                                                                access.role.toLowerCase(),
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </TableCell>
                                            <TableCell>
                                                {user.name
                                                    ? user.name
                                                    : 'NoName'}
                                            </TableCell>
                                            <TableCell>
                                                <>
                                                    {user.name
                                                        ? user.name
                                                        : 'NoName'}
                                                    in{' '}
                                                    {user.organizationId
                                                        ? user.organizationId
                                                        : 'NoOrganizationId'}
                                                    ;
                                                </>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                {pageCount > 1 && (
                    <Grid item xs={11} align='center'>
                        <Pagination
                            sx={{
                                my: 5,
                                justifyContent: 'center',
                                display: 'flex',
                            }}
                            count={pageCount}
                            variant='outlined'
                            shape='rounded'
                            page={+currentPage}
                            onChange={handlePagination}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
